<template>
    <div class="bg-white q-my-lg" id="benchmark-oee-chart">
        <div class="row bg-primary q-pa-sm q-px-md q-py-sm">
            <span class="text-h5 text-weight-medium text-white q-px-sm">
                {{ $t('Overall Equipment Effectiveness') }}
            </span>
        </div>
        <v-chart :option="chartOptions" autoresize class="graph" />
    </div>
</template>

<script>
import { BarChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    MarkLineComponent
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import { colors } from 'quasar';
const { getPaletteColor } = colors;
import { sanitizeString } from '@utils/sanitize.js';

use([
    CanvasRenderer,
    BarChart,
    TitleComponent,
    TooltipComponent,
    MarkLineComponent
]);

export default {
    name: 'BenchmarkOEEChart',
    components: { VChart },
    props: {
        chartData: {
            type: Object,
            required: true,
            validator: value => {
                const requiredKeys = [
                    'oee',
                    'performance',
                    'availability',
                    'quality'
                ];
                if (!requiredKeys.every(key => key in value)) {
                    return false;
                }

                const oee = value.oee;
                if (!Array.isArray(oee) || oee.length === 0) {
                    return false;
                }

                const performance = value.performance;
                if (!Array.isArray(performance) || performance.length === 0) {
                    return false;
                }

                const availability = value.availability;
                if (!Array.isArray(availability) || availability.length === 0) {
                    return false;
                }

                const quality = value.quality;
                if (!Array.isArray(quality) || quality.length === 0) {
                    return false;
                }

                return true;
            }
        },
        facilities: {
            type: Array,
            required: true,
            validator: value => {
                return Array.isArray(value) && value.length > 0;
            }
        }
    },
    data() {
        return {
            oeeData: this.chartData?.oee || [],
            performanceData: this.chartData?.performance || [],
            availabilityData: this.chartData?.availability || [],
            qualityData: this.chartData?.quality || []
        };
    },
    computed: {
        /**
         * Returns the basic series data for the chart.
         * @returns {Object} The series data.
         */
        seriesData() {
            return {
                type: 'bar',
                barGap: 0,
                emphasis: {
                    focus: 'series'
                },
                tooltip: {
                    valueFormatter: value => value + '%',
                    borderWidth: 0
                }
            };
        },
        /**
         * Returns the chart options.
         * @returns {Object} The chart options.
         */
        chartOptions() {
            return {
                animation: false,
                tooltip: {
                    trigger: 'item',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: params => {
                        return this.getTooltipContent(params);
                    }
                },
                legend: {
                    data: ['OEE', 'Performance', 'Availability', 'Quality'],
                    bottom: 0,
                    formatter: name => this.$t(name)
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '8%',
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                xAxis: [
                    {
                        type: 'category',
                        axisTick: { show: false },
                        data: this.facilities
                    }
                ],
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: '{value}%'
                        }
                    }
                ],
                series: [
                    {
                        name: 'OEE',
                        data: this.oeeData,
                        color: getPaletteColor('medium-blue'),
                        ...this.seriesData,
                        markLine: this.markLineData
                    },
                    {
                        name: 'Performance',
                        data: this.performanceData,
                        color: getPaletteColor('light-red'),
                        ...this.seriesData
                    },
                    {
                        name: 'Availability',
                        data: this.availabilityData,
                        color: getPaletteColor('warning'),
                        ...this.seriesData
                    },
                    {
                        name: 'Quality',
                        data: this.qualityData,
                        color: getPaletteColor('light-green'),
                        ...this.seriesData
                    }
                ]
            };
        },
        /**
         * Returns the mark line data for the chart.
         * If there is only one facility or the first value is 0, returns an empty object.
         * @returns {Object} The mark line data.
         */
        markLineData() {
            if (
                !this.oeeData ||
                this.oeeData[0] === 0 ||
                this.hasSingleFacility
            ) {
                return {};
            }
            return {
                data: [
                    {
                        yAxis: this.oeeData[0],
                        lineStyle: {
                            color: getPaletteColor('medium-grey'),
                            type: 'dashed'
                        },
                        label: {
                            show: true,
                            formatter: this.$t('Average OEE'),
                            position: 'insideEndTop'
                        },
                        tooltip: {
                            formatter: params =>
                                `${this.$t('Average OEE')}: <b>${params.value}%</b>`
                        }
                    }
                ]
            };
        },
        /**
         * Checks if there is only one facility.
         * @returns {boolean} True if there is only one facility.
         */
        hasSingleFacility() {
            return this.facilities && this.facilities.length === 1;
        }
    },
    methods: {
        /**
         * Gets the metric data based on the series name.
         * @param {string} seriesName - The name of the series.
         * @returns {Array} The metric data array.
         */
        getMetricData(seriesName) {
            switch (seriesName) {
                case 'OEE':
                    return this.oeeData;
                case 'Performance':
                    return this.performanceData;
                case 'Availability':
                    return this.availabilityData;
                case 'Quality':
                    return this.qualityData;
                default:
                    return [];
            }
        },
        /**
         * Returns the tooltip content for the chart.
         * @param {Object} params - The tooltip parameters.
         * @returns {string} The tooltip content.
         */
        getTooltipContent(params) {
            const activeFacility = params.name;
            const seriesName = params.seriesName;
            const marker = params.marker;
            const facilities = this.facilities;
            const metricData = this.getMetricData(seriesName);
            let metricsEl = '';
            for (let i = 0; i < facilities.length; i++) {
                const rawFacility = facilities[i];
                const escapedFacility = sanitizeString(rawFacility);
                const escapedMetric = sanitizeString(metricData[i]);
                const currentFacility =
                    rawFacility === activeFacility
                        ? `<b>${escapedFacility}</b>`
                        : escapedFacility;
                const currentMetric =
                    rawFacility === activeFacility
                        ? `<b>${escapedMetric}%</b>`
                        : `${escapedMetric}%`;

                const wrapperEl = `<div style="display:flex; justify-content: space-between; margin: 5px 0">`;
                const facilityEl = `<div>${marker} ${currentFacility}:</div>`;
                const metricEl = `<div style="margin-left:25px">${currentMetric}</div>`;
                metricsEl += `${wrapperEl}${facilityEl}${metricEl}</div>`;
            }

            const seriesEl = `<div><b>${sanitizeString(this.$t(seriesName))}</b></div>`;
            const metricsWrapperEl = `<div style="margin: 0 5px;">${metricsEl}</div>`;

            return `${seriesEl}${metricsWrapperEl}`;
        }
    }
};
</script>
<style lang="scss" scoped>
.graph {
    height: 500px;
    width: calc(100% - 2px);
    padding-bottom: 16px;
}
</style>
